import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { shareProgress } from '../utils/share';
import { trackSocialEvents } from '../utils/analytics';
import { BADGES } from '../utils/data';

const { FiShare2, FiX, FiCheck, FiAward } = FiIcons;

export const ShareAchievements = ({ 
  streak = 0, 
  longestFast = 0, 
  totalFasts = 0,
  earnedBadges = []
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState({
    streak: true,
    longestFast: true,
    totalFasts: false,
    badges: true
  });

  const toggleItem = (item) => {
    setSelectedItems(prev => ({
      ...prev,
      [item]: !prev[item]
    }));
  };

  const generateShareText = () => {
    const parts = [];
    
    if (selectedItems.streak && streak > 0) {
      parts.push(`🔥 ${streak} day streak`);
    }
    
    if (selectedItems.longestFast && longestFast > 0) {
      parts.push(`⏱️ ${longestFast.toFixed(1)}h longest fast`);
    }
    
    if (selectedItems.totalFasts && totalFasts > 0) {
      parts.push(`✅ ${totalFasts} completed fasts`);
    }
    
    if (selectedItems.badges && earnedBadges.length > 0) {
      const badgeEmojis = earnedBadges.map(b => b.icon).join('');
      parts.push(`${badgeEmojis} ${earnedBadges.length} badges earned`);
    }

    if (parts.length === 0) {
      return "Building metabolic flexibility with KetoMachine! 💪 #KetoMachine #Fasting";
    }

    return `My KetoMachine Progress:\n${parts.join('\n')}\n\nBuilding metabolic flexibility one fast at a time! 💪\n#KetoMachine #Fasting`;
  };

  const handleShare = () => {
    const text = generateShareText();
    const sharedItems = Object.keys(selectedItems).filter(key => selectedItems[key]);
    
    shareProgress({
      title: 'My KetoMachine Achievements',
      text
    });
    
    trackSocialEvents.achievementsShared(sharedItems);
    setIsOpen(false);
  };

  const hasAnyData = streak > 0 || longestFast > 0 || totalFasts > 0 || earnedBadges.length > 0;

  return (
    <>
      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(true)}
        className="px-5 py-3 bg-slate-900 text-white rounded-2xl text-xs font-black uppercase tracking-widest shadow-lg shadow-slate-200 flex items-center gap-2 active:bg-black transition-colors"
      >
        <SafeIcon icon={FiShare2} className="text-sm" />
        Share Progress
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", duration: 0.3 }}
              className="fixed inset-x-4 top-1/2 -translate-y-1/2 max-w-md mx-auto bg-white rounded-[2.5rem] shadow-2xl z-50 overflow-hidden"
            >
              <div className="bg-slate-900 text-white p-6 pb-8 relative">
                <button
                  onClick={() => setIsOpen(false)}
                  className="absolute top-6 right-6 p-2 hover:bg-white/10 rounded-xl transition-colors active:scale-90"
                >
                  <SafeIcon icon={FiX} className="text-xl" />
                </button>
                
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-white/10 rounded-2xl">
                    <SafeIcon icon={FiAward} className="text-2xl" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-black tracking-tight">Share Achievements</h2>
                    <p className="text-slate-400 text-xs font-medium mt-1">
                      Select what to include
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                {!hasAnyData ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">🎯</div>
                    <p className="text-slate-500 font-bold text-sm">
                      Start your first fast to unlock achievements!
                    </p>
                  </div>
                ) : (
                  <>
                    {streak > 0 && (
                      <AchievementCard
                        icon="🔥"
                        title="Current Streak"
                        value={`${streak} days`}
                        selected={selectedItems.streak}
                        onClick={() => toggleItem('streak')}
                      />
                    )}

                    {longestFast > 0 && (
                      <AchievementCard
                        icon="⏱️"
                        title="Longest Fast"
                        value={`${longestFast.toFixed(1)} hours`}
                        selected={selectedItems.longestFast}
                        onClick={() => toggleItem('longestFast')}
                      />
                    )}

                    {totalFasts > 0 && (
                      <AchievementCard
                        icon="✅"
                        title="Completed Fasts"
                        value={`${totalFasts} sessions`}
                        selected={selectedItems.totalFasts}
                        onClick={() => toggleItem('totalFasts')}
                      />
                    )}

                    {earnedBadges.length > 0 && (
                      <AchievementCard
                        icon={earnedBadges.map(b => b.icon).join('')}
                        title="Badges Earned"
                        value={`${earnedBadges.length} milestones`}
                        selected={selectedItems.badges}
                        onClick={() => toggleItem('badges')}
                      />
                    )}

                    <div className="mt-6 p-5 bg-slate-50 rounded-2xl border border-slate-100">
                      <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-3">
                        Preview
                      </div>
                      <p className="text-xs font-medium text-slate-600 leading-relaxed whitespace-pre-line">
                        {generateShareText()}
                      </p>
                    </div>
                  </>
                )}
              </div>

              {hasAnyData && (
                <div className="p-6 border-t border-slate-100 bg-slate-50/50">
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    onClick={handleShare}
                    className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black text-sm uppercase tracking-widest shadow-lg shadow-slate-200 flex items-center justify-center gap-2 active:bg-black transition-colors"
                  >
                    <SafeIcon icon={FiShare2} />
                    Share Now
                  </motion.button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

const AchievementCard = ({ icon, title, value, selected, onClick }) => (
  <motion.div
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className={`
      relative p-4 rounded-2xl border-2 cursor-pointer transition-all
      ${selected 
        ? 'bg-blue-50 border-blue-500' 
        : 'bg-white border-slate-200 hover:border-slate-300'
      }
    `}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className={`
          text-2xl p-2 rounded-xl transition-all
          ${selected ? 'bg-blue-100 scale-110' : 'bg-slate-50'}
        `}>
          {icon}
        </div>
        <div>
          <div className={`
            font-black text-sm
            ${selected ? 'text-slate-900' : 'text-slate-600'}
          `}>
            {title}
          </div>
          <div className={`
            text-xs font-bold mt-0.5
            ${selected ? 'text-blue-600' : 'text-slate-400'}
          `}>
            {value}
          </div>
        </div>
      </div>

      <div className={`
        w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all
        ${selected 
          ? 'bg-blue-500 border-blue-500' 
          : 'bg-white border-slate-300'
        }
      `}>
        {selected && (
          <SafeIcon icon={FiCheck} className="text-white text-xs" />
        )}
      </div>
    </div>
  </motion.div>
);